import { useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { ResetPassword } from "../../../services";
import { useAuth, useStore } from "../../../hooks";
import { Eye } from "react-bootstrap-icons";
import { Toast } from "../../Toast";

type FormFields = {
  verificationCode: string;
  newPassword: string;
  confirmPassword: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  handleClose: () => void;
  handleSuccess?: () => void;
  handleSwitch: (newMode: any) => void;
};

const ResetPasswordModal: React.FC<Props> = ({
  show,
  handleClose,
  handleSwitch,
}) => {
  const { storeId } = useStore();
  const { customer } = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormFields>({
    verificationCode: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const prevValue = formData[name as keyof typeof formData];

    const meaningfulChange = value.trim() !== prevValue?.toString().trim();
    if (meaningfulChange) {
      setErrors((prev) => ({ ...prev, [name]: "" })); // Clear the error when user types
    }

    setFormData({ ...formData, [name]: value.trim() });
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    setValidated(false);

    if (!formData.verificationCode) {
      newErrors.verificationCode = "Please enter the verification code";
    }
    // else if (formData.verificationCode.length < 6) {
    //   newErrors.verificationCode = "Password must be at least 6 characters long";
    // }

    if (!formData.newPassword) {
      newErrors.newPassword = "Please enter your new password";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters long";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (formData.confirmPassword.length < 6) {
      newErrors.confirmPassword = "Password must be at least 6 characters long";
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return false;

    try {
      setIsLoading(true);

      const { status, message } = await ResetPassword({
        store_id: storeId,
        email: customer.email.toLowerCase(),
        new_password: formData.newPassword,
        new_password_confirmation: formData.confirmPassword,
        verification_code: formData.verificationCode,
      });

      if (status) {
        handleSwitch("login");
      } else {
        Toast.error(message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }

    // handleSuccess();
  };

  return (
    <>
      <Modal
        className="reset-password modal-mobile-bottom"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h4">Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            className="login_main"
            method="POST"
            validated={validated}
            onSubmit={handleSubmit}
            noValidate
          >
            <Form.Group className="mb-3">
              <Form.Label>
                Verification Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter verification code"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleChange}
                isInvalid={!!errors.verificationCode}
                required
              />
              <Form.Text muted>
                Enter code sent to {customer.email.toLowerCase()}
              </Form.Text>
              {errors.verificationCode && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.verificationCode}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="position-relative mb-3">
              <Form.Label>
                New Password <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                isInvalid={!!errors.newPassword}
                required
              />
              <span
                className="position-absolute cursor-pointer text-muted"
                style={{ right: "15px", top: "32px" }}
                onClick={togglePasswordVisibility}
              >
                <Eye />
              </span>
              {errors.newPassword && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.newPassword}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="position-relative mb-4">
              <Form.Label>
                Confirm New Password <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                isInvalid={!!errors.confirmPassword}
                required
              />
              <span
                className="position-absolute cursor-pointer text-muted"
                style={{ right: "15px", top: "32px" }}
                onClick={togglePasswordVisibility}
              >
                <Eye />
              </span>
              {errors.confirmPassword && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <button type="submit" className="login_btn" disabled={isLoading}>
              {isLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Continue"
              )}
            </button>
            <div className="register_colm">
              <span
                className="cursor-pointer"
                onClick={() => handleSwitch("login")}
              >
                Back to Login
              </span>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ResetPasswordModal;
